import { supabase } from '../supabaseClient';
import { sendEmail, documentPublishedEmail } from './email';
import { fetchActivePortalUsersBySupplier } from './briefRecipients';

// Eligible-recipient list for the notify picker: active, non-record-only
// suppliers that have at least one active portal user to email. Returns
// [{ id, supplier_name, userCount }] sorted by name.
export async function fetchNotifiableSuppliers() {
  const { data: suppliers } = await supabase
    .from('suppliers')
    .select('id, supplier_name')
    .eq('status', 'active')
    .eq('is_record_only', false)
    .order('supplier_name');
  const list = suppliers || [];
  if (list.length === 0) return [];
  const usersBySupplier = await fetchActivePortalUsersBySupplier(list.map(s => s.id));
  return list
    .map(s => ({ id: s.id, supplier_name: s.supplier_name, userCount: (usersBySupplier.get(s.id) || []).length }))
    .filter(s => s.userCount > 0);
}

// Fan out a "new document available" email to every active, non-record-only
// supplier's active portal users, when a 'shown' document is published with
// Notify & publish. Mirrors alertAdminsIfNonCompliantOnBriefAdd
// (src/lib/complianceAlert.js) and RiskFlagMatrix.handleNotify, but targets
// PORTAL USERS (supplier_users) — the same routing every brief email uses —
// rather than the stored main/secondary contacts.
//
// Best-effort: a publish has already succeeded by the time this runs, so a
// per-recipient mail failure is swallowed and the fan-out continues. One
// supplier_email_log row is written per supplier (email_kind
// 'document_published') so the send surfaces in the supplier Activity tab.
//
// When `testEmail` is set, this is a TEST send: one email to that address only,
// so an admin can preview the notification before the real blast. No supplier
// fan-out and no supplier_email_log row is written. Returns { test: true, ... }.
//
// When `supplierIds` is a non-null array, only those suppliers are notified
// (from the eligible set); null/undefined notifies all eligible suppliers.
//
// Returns { suppliersLogged, emailsSent, failures } for a caller toast.
export async function notifyDocumentToSuppliers({ documentLabel, sentByUserId = null, testEmail = null, supplierIds = null }) {
  const result = { suppliersLogged: 0, emailsSent: 0, failures: [], test: false };
  if (!documentLabel) return result;

  if (testEmail) {
    result.test = true;
    result.testEmail = testEmail;
    const { subject, html } = documentPublishedEmail({
      contactName: null,
      documentLabel,
      portalUrl: `${window.location.origin}/portal`,
    });
    try {
      await sendEmail({ to: testEmail, subject, html });
      result.emailsSent = 1;
    } catch (e) {
      result.failures.push(`test → ${testEmail}`);
    }
    return result;
  }

  let query = supabase
    .from('suppliers')
    .select('id, supplier_name')
    .eq('status', 'active')
    .eq('is_record_only', false);
  if (Array.isArray(supplierIds)) {
    if (supplierIds.length === 0) return result; // explicit empty selection
    query = query.in('id', supplierIds);
  }
  const { data: suppliers } = await query;
  const list = suppliers || [];
  if (list.length === 0) return result;

  const usersBySupplier = await fetchActivePortalUsersBySupplier(list.map(s => s.id));
  if (usersBySupplier.size === 0) return result;

  const portalUrl = `${window.location.origin}/portal`;

  for (const supplier of list) {
    const users = usersBySupplier.get(supplier.id) || [];
    if (users.length === 0) continue; // no one to notify for this supplier
    const sentTo = [];
    for (const u of users) {
      const { subject, html } = documentPublishedEmail({
        contactName: u.full_name,
        documentLabel,
        portalUrl,
      });
      try {
        await sendEmail({ to: u.email, subject, html });
        result.emailsSent++;
        sentTo.push(u.email);
      } catch (e) {
        result.failures.push(`${supplier.supplier_name} → ${u.email}`);
      }
    }
    if (sentTo.length > 0) {
      const { error: logErr } = await supabase.from('supplier_email_log').insert([{
        supplier_id: supplier.id,
        email_kind: 'document_published',
        recipients: sentTo,
        sent_by: sentByUserId || null,
      }]);
      if (!logErr) result.suppliersLogged++;
    }
  }

  return result;
}
